import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Segmented, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';

import useDateFormat from '../../hooks/useDateFormat';
import { RecordIdentity, RecordTitle } from '../common/RecordBadge';
import { actionGlyph } from '../common/actionMenu';
import { CHEVRON_LEFT, CHEVRON_RIGHT, CLOSE, KEBAB, OPEN, PLAY } from '../icons';
import { WIDE_DISABLED_REASON } from '../../hooks/usePaneWidth';
import { NO_EXCLUSIONS, useItemActions } from './ItemActionsMenu';
import ItemAuditLine from './ItemAuditLine';
import ItemReadView from './ItemReadView';
import ItemForm from './ItemForm';
import { api } from '../../api/client';
import { API_ITEMS_URL } from '../../utils';
import { normalizeId } from '../../utils/ids';
import { lockStateFor, recordPolicyFor } from '../../utils/recordPolicy';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { LINKED_ROW_EXCLUDED_ACTIONS, isLinkedRow, linkedFromOf, rowSourceOf } from '../../utils/linkedRecords';
import useLinkedSources from '../../hooks/useLinkedSources';
import { rowSourceIdentity } from '../../utils/recordQualifier';

/**
 * The item detail: identity block on top, the read view in the middle, the
 * two doors at the bottom. Two variants of the one component:
 *
 *   pane    — the desktop right-hand pane (docked ≥1200px, a sheet below),
 *             opened by card click/Enter, closed by Esc or the X.
 *   inline  — the same content rendered INSIDE the selected list card on
 *             phones, where the card grows into the detail.
 *
 * Every item action is reachable here through the ⋯ menu (the full platform
 * set — see useItemActions). The footer carries the two doors the design
 * names — Edit (600) and Duplicate — beside the keyboard hint; Enter edits.
 * When the card has duplicate profiles, Duplicate is a split button: the
 * click runs the card's default profile (or a plain copy when none is the
 * default), the arrow lists every profile and "Plain duplicate".
 *
 * Under the pane's title a strip (KPR-1643; owner, 2026-10-30) holds ‹ ›,
 * the previous and next item (as ↑/↓), the width (Narrow · Medium · Wide,
 * `[` and `]` on the page) and Open, the item's own page. The widths are the
 * page's (usePaneWidth); the pane only draws the choice and says it.
 *
 * EDIT MODE (KPR-1644; owner ruling i10). Edit, and Enter on the page, turn
 * the pane into the item form — the item page's own `ItemForm`, every rule
 * with it — at the pane's width, the list staying where it is. The form's ⋯
 * and the card's action buttons take the title row's place of the read
 * view's ⋯; Cancel · Save take the foot. Leaving with unsaved edits asks
 * Save · Discard · Stay (hooks/usePaneFormLeave). A save shows the item as
 * read again and hands it to the list (`onItemUpdated`). Edit is offered only
 * where the person may change this record here: not a reader, not a linked or
 * read-only row (Open instead, as before), not a locked record. On a phone
 * (`editInPlace` false) Edit still opens the item page. The page reads the
 * pane's state through `controlRef` ({ editing, enter }), for Enter and Esc.
 */
const PANE_WIDTH_OPTIONS = [
	{ value: 'narrow', label: 'Narrow', title: 'Narrow: one column' },
	{ value: 'medium', label: 'Medium', title: 'Medium: short fields two to a row' },
	{ value: 'wide', label: 'Wide', title: 'Wide: the form layout as arranged' },
];

export default function ItemDetailPane({
	item: itemProp,
	card,
	elementDefs,
	resolveLookupValue,
	collectionId,
	collection = null,
	onClose,
	canEdit = true,
	// Manage on this collection: the unlock action, and nothing else.
	canManage = false,
	// The card cache, so a record policy inherited from a parent card is read
	// the same way here as on the list.
	cardsById = null,
	variant = 'pane',
	onDeleted,
	// An intake submission decided here: (decision, result).
	onIntakeReviewed = null,
	// A record read-only in this list for a reason the item alone does not
	// say — a parent collection's record opened here by reference
	// (utils/linkedRecords isReadOnlyRow): it offers what a linked row does.
	readOnlyRow = false,
	// The item as it stands after a tag change (the read view's chips, the ⋯
	// Tag… dialog): the page's feed puts it in the row's place.
	onItemUpdated = null,
	// Moved by the list's Refresh (KPR-1346): the read view's own sections
	// (related, attachments, comments) read again with the list.
	reloadKey = 0,
	// The pane's width (KPR-1643): `width` drawn, `onWidth(next)` to choose,
	// `wideAllowed` false while the list area is too narrow for Wide. No
	// `onWidth`: no width control (a surface without the choice).
	width = 'medium',
	wideAllowed = true,
	onWidth = null,
	// ‹ ›: `onStep(-1)` / `onStep(1)` move to the previous / next item.
	onStep = null,
	// Edit here (KPR-1644), or on the item page (a phone).
	editInPlace = true,
	// The page's handle on the pane: { editing, enter() | null }.
	controlRef = null,
}) {
	useDateFormat();
	// The item as the last save here read it back, until the list's copy is as new:
	// an item the feed does not hold (opened by address) shows what was saved too.
	const [savedCopy, setSavedCopy] = useState(null);
	const item = savedCopy && normalizeId(savedCopy._id) === normalizeId(itemProp?._id)
		&& String(savedCopy.updatedAt || '') > String(itemProp?.updatedAt || '') ? savedCopy : itemProp;
	// The latest save's read wins over an earlier one answering late.
	const readSeqRef = useRef(0);
	// The item whose form is open; another item opens in the read view.
	const [editingId, setEditingId] = useState(null);
	const [headerSlot, setHeaderSlot] = useState(null);
	const [footerSlot, setFooterSlot] = useState(null);
	// Bumped when an action relates something so the read view's Related
	// section reloads in place.
	const [refreshKey, setRefreshKey] = useState(0);

	const navigate = useNavigate();
	// The list's collection, for where a row handed in without one lives (a
	// read-only row carries `collection={null}`).
	const contextCollection = useCollectionContext()?.collection || null;
	const rowSource = rowSourceOf(item, collection || contextCollection);
	// A record a collection link brought into this list: read-only here,
	// opened (never edited) in its own collection.
	const linked = isLinkedRow(item);
	// Its files are the source's to hand over: downloadable here only when
	// the link it came through lets this collection keep copies (keepr-api
	// § Collection links, 403 copies_not_allowed otherwise).
	const linkedSources = useLinkedSources(collectionId, { enabled: linked });
	const linkedAllowsCopies = linked
		? Boolean(linkedSources.sources.get(linkedFromOf(item)?.collectionId)?.allowCopies)
		: undefined;
	const { actions, menu, modals, run, title, cardType, duplicateMenu, defaultDuplicateProfile } = useItemActions({
		item,
		card,
		elementDefs,
		resolveLookupValue,
		collectionId,
		collection,
		canEdit,
		canManage,
		cardsById,
		surface: variant,
		exclude: linked || readOnlyRow ? LINKED_ROW_EXCLUDED_ACTIONS : NO_EXCLUSIONS,
		onDeleted,
		onRelated: () => setRefreshKey(key => key + 1),
		onItemUpdated,
	});

	const itemId = item?._id;
	const canDuplicate = Array.isArray(actions) && actions.some(action => action?.key === 'duplicate' && !action.hidden && !action.disabled);
	const editPath = collectionId && itemId ? `/collections/${collectionId}/items/${itemId}` : null;

	// Whether this person may change this record here: a writer of its card, not
	// a linked or read-only row, and not a locked record (read through the card's
	// chain, as the form and the ⋯ read it).
	const cardId = normalizeId(item?.card_id) || normalizeId(card?._id);
	const locked = lockStateFor(item, recordPolicyFor(cardsById?.[cardId] ? cardsById : (card && cardId ? { [cardId]: card } : {}), cardId)).locked;
	const canEditHere = variant === 'pane' && Boolean(editPath) && canEdit && !linked && !readOnlyRow && !locked;
	// Only the start is gated: a form already open stays open when the record
	// locks under it or the access changes, and says so itself (its notice,
	// its read-only fields) rather than vanishing with what was typed.
	const editing = variant === 'pane' && Boolean(itemId) && editingId === itemId;
	const startEdit = useCallback(() => setEditingId(itemId), [itemId]);
	// Another item opens in the read view, and coming back to this one does too
	// (an Enter that reached the old item's handle just before the move included).
	useEffect(() => {
		setEditingId(prev => (prev && prev !== itemId ? null : prev));
	}, [itemId]);
	const openPage = useCallback(() => { if (editPath) navigate(editPath); }, [editPath, navigate]);
	const edit = canEditHere ? (editInPlace ? startEdit : openPage) : (linked && editPath ? openPage : null);
	if (controlRef) controlRef.current = { editing, enter: edit };
	useEffect(() => () => { if (controlRef) controlRef.current = null; }, [controlRef]);

	// A save landed: the read view at once with what was sent, then the item as
	// keepr reads it now (computed values, tags), handed to the list too.
	const handleSaved = useCallback(async (data) => {
		setEditingId(null);
		if (data?._id) setSavedCopy({ ...item, ...data, elements: { ...(item?.elements || {}), ...(data.elements || {}) } });
		const seq = readSeqRef.current + 1;
		readSeqRef.current = seq;
		try {
			const { data: fresh } = await api.get(`${API_ITEMS_URL}/${itemId}`);
			if (!fresh?._id || seq !== readSeqRef.current) return;
			setSavedCopy(fresh);
			onItemUpdated?.(fresh);
		} catch {
			if (data?._id) onItemUpdated?.({ ...item, ...data });
		}
	}, [item, itemId, onItemUpdated]);

	// The audit line's "View history" link is the menu's History… action;
	// only offered when that action is on the menu (the API gates history on
	// write).
	const canHistory = Array.isArray(actions) && actions.some(action => action?.key === 'history' && !action.hidden && !action.disabled);

	// A Symptom walks its tree from here and from the ⋯ menu (KPR-491).
	const canWalk = Array.isArray(actions) && actions.some(action => action?.key === 'walk' && !action.hidden);

	const Root = variant === 'inline' ? 'div' : 'aside';
	const classes = ['item-pane', variant === 'inline' ? 'item-pane--inline' : `item-pane--${width}`].filter(Boolean).join(' ');
	const widthOptions = PANE_WIDTH_OPTIONS.map(option => (option.value === 'wide' && !wideAllowed
		? { ...option, disabled: true, title: WIDE_DISABLED_REASON }
		: option));

	return (
		<Root className={classes} aria-label="Item details" data-pane-width={variant === 'inline' ? undefined : width}>
			{/* Inline (inside the list card) the card's own header already
			    carries the badge, the ⋯ menu, and the close button. */}
			{variant === 'inline' ? null : (
			<RecordIdentity
				className="item-pane__header"
				badge={(
					// The pane is the item's own surface: the plain two-line
					// title — the card name muted over the item's title beside
					// the chip — no pill.
					// A record that lives elsewhere (a linked row, a family or
					// parent record) is qualified by its collection, the
					// relation said by the segment (a linked row's lock), its
					// tooltip and its spoken words.
					<RecordTitle
						kind="item"
						record={card}
						kicker={cardType}
						title={title}
						size="lg"
						wrap
						titleAs="h2"
						collection={rowSourceIdentity(rowSource)}
						collectionRelation={rowSource?.via || null}
					/>
				)}
				below={(
					<ItemAuditLine item={item} onHistory={canHistory ? () => run('history') : undefined} />
				)}
				actions={editing ? (
					<>
						{/* The form's own ⋯ and the card's buttons (ItemFormHeader). */}
						<span className="item-pane__form-actions" ref={setHeaderSlot} />
						<Button type="text" icon={<CLOSE />} aria-label="Close details" onClick={onClose} />
					</>
				) : (
					<>
						{menu ? (
							<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
								<Button icon={<KEBAB />} aria-label="Item actions" />
							</Dropdown>
						) : null}
						{/* Esc also closes; the button is the touch/screen-reader door. */}
						<Button
							type="text"
							icon={<CLOSE />}
							aria-label="Close details"
							onClick={onClose}
						/>
					</>
				)}
			/>
			)}

			{variant === 'pane' && (onStep || onWidth || editPath) ? (
				<div className="item-pane__strip">
					{onStep ? (
						<span className="item-pane__step">
							<Tooltip title="Previous item (↑)">
								<Button type="text" size="small" icon={<CHEVRON_LEFT />} aria-label="Previous item" onClick={() => onStep(-1)} />
							</Tooltip>
							<Tooltip title="Next item (↓)">
								<Button type="text" size="small" icon={<CHEVRON_RIGHT />} aria-label="Next item" onClick={() => onStep(1)} />
							</Tooltip>
						</span>
					) : null}
					{onWidth ? (
						<span className="item-pane__width">
							<Segmented
								size="small"
								aria-label="Pane width"
								value={width}
								options={widthOptions}
								onChange={onWidth}
							/>
							{wideAllowed ? null : <span className="item-pane__width-reason">{WIDE_DISABLED_REASON}</span>}
						</span>
					) : null}
					{editPath ? (
						<Tooltip title="Open the item page">
							<Button
								type="text"
								size="small"
								className="item-pane__open"
								icon={<OPEN />}
								aria-label="Open the item page"
								onClick={() => navigate(editPath)}
							/>
						</Tooltip>
					) : null}
				</div>
			) : null}

			{editing ? (
				<div className="item-pane__fields item-pane__form">
					<ItemForm
						variant="pane"
						itemId={itemId}
						collectionId={collectionId}
						headerSlot={headerSlot}
						footerSlot={footerSlot}
						onSaved={handleSaved}
						onCancel={() => setEditingId(null)}
						onDeleted={() => onDeleted?.(itemId)}
					/>
				</div>
			) : (
			<div className="item-pane__fields">
				{canWalk ? (
					<Button className="item-pane__walk" icon={<PLAY />} onClick={() => run('walk')}>
						Start walk-through
					</Button>
				) : null}
				<ItemReadView
					item={item}
					card={card}
					cardsById={cardsById}
					elementDefs={elementDefs}
					resolveLookupValue={resolveLookupValue}
					collectionId={collectionId}
					collection={collection}
					canEdit={canEdit}
					refreshKey={refreshKey + reloadKey}
					onIntakeReviewed={onIntakeReviewed}
					canDownloadAttachments={linkedAllowsCopies}
					// A parent's record read by reference opens nothing further
					// (never a second hop): no Related section of its own.
					showRelated={rowSource?.via !== 'parent'}
					// Nor does it open its comments: the API's read of a parent
					// record by reference does not reach them.
					comments={rowSource?.via !== 'parent'}
					onItemUpdated={onItemUpdated}
				/>
			</div>
			)}

			{variant === 'pane' && editing ? (
				// Cancel · Save, drawn here by the form (ItemFormPaneFooter).
				<div className="item-pane__footer" ref={setFooterSlot} />
			) : null}
			{variant === 'pane' && !editing ? (
				<div className="item-pane__footer">
					<div className="item-pane__footer-actions">
						{edit ? (
							<Button
								className="item-pane__edit"
								icon={actionGlyph(linked ? 'open' : 'edit')}
								onClick={edit}
							>
								{linked ? 'Open' : 'Edit'}
							</Button>
						) : null}
						{canDuplicate && duplicateMenu ? (
							<Dropdown.Button
								className="item-pane__duplicate"
								menu={duplicateMenu}
								trigger={['click']}
								placement="topRight"
								onClick={() => run('duplicate')}
								title={defaultDuplicateProfile ? `Duplicates with “${defaultDuplicateProfile.name}”` : undefined}
								buttonsRender={([left, right]) => [
									React.cloneElement(left, { icon: actionGlyph('duplicate') }),
									React.cloneElement(right, { 'aria-label': 'Duplicate with a profile' }),
								]}
							>
								Duplicate
							</Dropdown.Button>
						) : null}
						{canDuplicate && !duplicateMenu ? (
							<Button icon={actionGlyph('duplicate')} onClick={() => run('duplicate')}>
								Duplicate
							</Button>
						) : null}
					</div>
					<span className="item-pane__hint" aria-hidden="true">
						{edit ? `↑↓ move · Enter ${linked ? 'open' : 'edit'}` : '↑↓ move'}
					</span>
				</div>
			) : null}

			{modals}
		</Root>
	);
}

/**
 * The pane of an item that is no longer there (KPR-1346): it was deleted, or
 * the person can no longer read it, since the list last showed it. Said in
 * the pane's place rather than leaving the old copy open; Close (or Esc)
 * lets go of the selection.
 */
export function ItemPaneGone({ onClose }) {
	return (
		<aside className="item-pane item-pane--gone" aria-label="Item details">
			<div className="item-pane__gone" role="status">
				<p>This item was deleted, or you can no longer see it.</p>
				<Button onClick={onClose}>Close</Button>
			</div>
		</aside>
	);
}
